/**
 * The third payload format: a Pine Labs device's built-in printer. Port of mobile
 * `helper/twoInchBillPrintHelper.ts` `createBillPrintMap` (:115-427), the canonical copy.
 *
 * Pine Labs terminals take neither ESC/POS bytes nor token text — their SDK takes an ARRAY of
 * line objects, each with its own width and alignment, and lays the receipt out itself. So this is
 * not an encoder over the library's content: it is a SECOND layout of the same sale, which is why
 * a Pine Labs bill has never looked like any other Rista bill. Kept exactly as v1 draws it,
 * including where v1 is odd — those places are commented.
 *
 * The transport hands the array to the native module; nothing here talks to a device.
 */

import type { PrintLine } from "../types";

/** Every line v1 writes is 32 characters wide — the paper is two inches. */
const WIDTH = 32;

function remainingSpaces(length: number, width = WIDTH): string {
  return length >= width ? "" : " ".repeat(width - length);
}

/**
 * v1 `getSpaceSeperateStr`: pad so the LAST element is pushed right. It measures the total length
 * of every element, so three elements pad by the sum — which is what makes an item line's price
 * column land where it does. Ported with the quirk, not around it.
 */
function spaceSeparated(parts: string[], width = WIDTH): string {
  const total = parts.reduce((sum, part) => sum + String(part).length, 0);
  return parts.reduce((acc, part, index) => `${acc}${index === parts.length - 1 ? remainingSpaces(total, width) : ""}${part}`);
}

function line(text: string, centered = false): PrintLine {
  return { PrintDataType: 0, PrinterWidth: WIDTH, IsCenterAligned: centered, DataToPrint: text };
}

const emptyLine = (): PrintLine => line("\n", true);
const separator = (): PrintLine => line("--------------------------------", true);

/**
 * `moment(date).format("YYYY-MM-DD")` without moment: parse, then read the LOCAL calendar date, as
 * moment's default does. The package has no date dependency and wants none.
 */
function ymd(value: unknown): string {
  const date = new Date(value as string);
  if (Number.isNaN(date.getTime())) return "Invalid date"; // moment prints this too
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** v1 `getItemPrintData`: the item's name wrapped into the space its quantity and price leave. */
function itemLines(item: any): string[] {
  const left = item.quantity + "x" + item.netAmount + " ";
  const name = String(item.shortName ?? "");
  const SPACE_FOR_PRICE = 9;
  // v1 lets this reach 0 or below for a 23+ character quantity-and-amount column, and then its
  // wrap loop never shortens the name — the device hangs. At least one character per line.
  const spaceForName = Math.max(1, WIDTH - SPACE_FOR_PRICE - left.length);

  const lines: string[] = [];
  let end = Math.min(spaceForName, name.length);
  lines.push(spaceSeparated([left, name.slice(0, end), item.netAmount]));

  let rest = name.slice(end);
  while (rest.length) {
    end = Math.min(spaceForName, rest.length);
    lines.push(" ".repeat(left.length) + rest.slice(0, end));
    rest = rest.slice(end);
  }
  return lines;
}

export function encodeLines(sale: any): PrintLine[] {
  const data = sale || {};
  const out: PrintLine[] = [];

  if (data.billHeader && Object.keys(data.billHeader).length) {
    out.push(line(data.billHeader.brandName, true));
    out.push(line(data.billHeader.branchName, true));
    out.push(line(data.billHeader.businessName, true));
    // v1 prints the phone number twice — the tax-id line reads `phoneNumber`. Kept.
    out.push(line(data.billHeader.phoneNumber, true));
    if (data.billHeader.taxId) out.push(line(data.billHeader.phoneNumber, true));
  }
  out.push(emptyLine());

  if (data.billTitle) out.push(line(data.billTitle, true));
  if (data.channelName) out.push(line(data.channelName, true));
  if (data.serviceLabel?.label) out.push(line(data.serviceLabel.label, true));
  out.push(separator());

  if (data.billNumber) out.push(line(spaceSeparated(["Invoice No:", data.billNumber])));
  if (data.modifiedBy && Object.keys(data.modifiedBy).length) {
    out.push(line(spaceSeparated(["Invoice Date:", ymd(data.modifiedBy.date)])));
    out.push(line(spaceSeparated(["Sale By:", data.modifiedBy.userName.firstName])));
  }
  if (data.totalQty) out.push(line("Sale Item Count: " + data.totalQty + " item (" + data.totalQty + " Qty)"));
  out.push(separator());

  const items: any[] = data.saleItems || [];
  for (const item of items) {
    for (const text of itemLines(item)) out.push(line(text));
    for (const option of item.itemOptionList || []) {
      out.push(line("addon:  " + option.quantity + "x" + option.amount + " " + option.name + "     " + option.amount));
    }
    for (const discount of item.itemDiscountList || []) {
      out.push(line("discount:  " + discount.discountCode + "           " + discount.amount));
    }
  }
  out.push(separator());

  out.push(line(spaceSeparated(["Subtotal", `Rs.${data.billSubTotal}`])));
  for (const charge of data.chargeList || []) out.push(line(spaceSeparated([charge.name, `Rs.${charge.amount}`])));
  for (const discount of data.discountList || []) out.push(line(spaceSeparated([discount.discountCode, `Rs.${discount.amount}`])));
  for (const tax of data.billTaxList || []) out.push(line(spaceSeparated([tax.name, `Rs.${tax.amount}`])));
  out.push(line(spaceSeparated(["Bill Total", `Rs.${data.billTotal}`])));
  out.push(line(spaceSeparated(["Bill Total(rounded)", `Rs.${data.billRoundedAmount}`])));
  out.push(separator());

  if (data.paymentInfo?.payments) {
    out.push(line("Payment Summary", true));
    for (const payment of data.paymentInfo.payments) {
      out.push(line(spaceSeparated([payment.mode, `Rs.${payment.amount}`])));
    }
  }

  out.push(separator());
  out.push(line("Thank you! Visit Again", true));
  out.push(line("Powered by www.dotpe.in", true));
  out.push(separator());
  out.push(emptyLine());
  return out;
}
